import { Box, Button, Card, CardContent, Checkbox, Divider, Grid, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { priceFormatter } from '~/utils/priceFormatter';
import { getDisplayProductPrice } from '~/utils/promotionUtils';
import { FinancialData } from '../../interfaces/financial-data';
import { Resume } from '../resume';

interface ContractItem {
  product_id: number;
  product_name: string;
  product_description: string;
  product_price: number;
  cover: {
    cover_name: string;
    cover_description: string;
    cover_icon: string;
  }[];
}

interface Option {
  isDetailsOpen: boolean;
}

type ContractItemWithOption = ContractItem & Option;

interface Props {
  contract_item: ContractItem[] | undefined;
  products: number[];
  handleProductChange: (productId: number) => Promise<void>;
  isLoading: boolean;
  financialData: FinancialData | undefined;
}

export function ContractCard({ contract_item, products, handleProductChange, isLoading, financialData }: Props) {
  if (!contract_item) return null;

  const [contractItems, setContractItems] = useState([] as ContractItemWithOption[]);

  function toggleDetails(index: number) {
    setContractItems((prev) => {
      const newState = [...prev];
      newState[index].isDetailsOpen = !newState[index].isDetailsOpen;

      return newState;
    });
  }

  function mergeOptions(contractItems: ContractItem[]): ContractItemWithOption[] {
    return contractItems.map((contractItem) => ({
      ...contractItem,
      isDetailsOpen: false
    }));
  }

  useEffect(() => {
    setContractItems(mergeOptions(contract_item));
  }, [contract_item]);

  return (
    <Card
      sx={{
        margin: { xs: '2rem 0.5rem', sm: '2rem 1rem', md: '2rem' },
        borderRadius: 2
      }}
    >
      <CardContent sx={{ padding: { xs: '1rem', sm: '1rem 1.5rem' } }}>
        <Typography variant="subtitle1" sx={{ paddingBottom: 1, marginLeft: 4 }}>
          Detalhes da contratação
        </Typography>
        <Divider />
        {contractItems.map((contractItem, index) => {
          return (
            <Box
              key={contractItem.product_id}
              sx={{
                marginY: 2,
                borderRadius: 5,
                border: '2px solid #a547ff',
                padding: 1,
                background: '#1e1e1e'
              }}
            >
              <Grid container justifyContent={'space-between'} alignItems={'center'}>
                <Grid item xs={12} sm={6}>
                  <Box display={'flex'} alignItems={'center'}>
                    <Checkbox
                      checked={products.includes(contractItem.product_id)}
                      onChange={() => handleProductChange(contractItem.product_id)}
                      color="success"
                    />
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color="primary"
                      sx={{
                        fontSize: { xs: '1rem', sm: '1.25rem' }
                      }}
                    >
                      {contractItem.product_name}
                    </Typography>
                  </Box>
                  <Button
                    variant="text"
                    sx={{
                      paddingY: 0,
                      color: '#54c556',
                      fontSize: '0.9rem',
                      fontWeight: 700
                    }}
                    onClick={() => toggleDetails(index)}
                  >
                    {contractItem.isDetailsOpen ? '-' : '+'} detalhes
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="primary"
                    align="right"
                    sx={{
                      fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}
                  >
                    + 12x R$
                    {priceFormatter(getDisplayProductPrice(contractItem.product_id, contractItem.product_price))}
                  </Typography>
                </Grid>
              </Grid>
              {contractItem.isDetailsOpen && <Typography>{contractItem.product_description}</Typography>}
            </Box>
          );
        })}

        <Divider />
        <Resume financialData={financialData} isLoading={isLoading} />
      </CardContent>
    </Card>
  );
}
